import { Component, inject, OnInit, signal } from '@angular/core';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SidebarModule } from 'primeng/sidebar';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AdminApiService } from '../admin-api.service';
import {
  UserListItem,
  UserDetails,
  CreateUserRequest,
  UpdateUserRequest,
  Role,
  Group,
  AuditLog,
  UserSession,
  PagedResult,
  EmployeeListItem,
  BranchListItem,
  SystemApp,
  InsuranceCompanyListItem,
  ExternalEntityListItem,
} from '../models';

type PortalEntityType = 'patient' | 'doctor' | 'referrallab';

@Component({
  standalone: true,
  selector: 'app-users-page',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SidebarModule,
    InputTextModule,
    DropdownModule,
    MultiSelectModule,
    ToggleButtonModule,
    TabViewModule,
    TagModule,
    ChipModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    TranslateModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './users.page.html',
  styleUrls: ['./users.page.scss'],
})
export class UsersPage implements OnInit {
  api = inject(AdminApiService);
  ms = inject(MessageService);
  confirmService = inject(ConfirmationService);
  private route = inject(ActivatedRoute);

  /** من مسار «مستخدمي التأمين»: فلترة API + سلوك النموذج */
  readonly portalSystemKey = signal<string | null>(null);
  readonly portalEntityType = signal<PortalEntityType | null>(null);
  readonly relaxedEmployeeBranch = signal(false);
  readonly pageTitleKey = signal('USERS.TITLE');
  readonly pageSubtitleKey = signal('USERS.SUBTITLE');

  // Table data
  users = signal<UserListItem[]>([]);
  totalRecords = signal(0);
  loading = signal(false);

  // Filters
  filters = {
    search: '',
    status: '',
    roleIds: [] as string[],
    groupIds: [] as string[],
    dateFrom: '',
    dateTo: '',
    lastLoginFrom: '',
    lastLoginTo: '',
    includeDeleted: false,
  };

  // Pagination & Sorting
  pagination = {
    first: 0,
    rows: 20,
    page: 1,
    sortField: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc',
  };

  // Roles & Groups for filters and forms
  roles: Role[] = [];
  groups: Group[] = [];
  roleOptions: { label: string; value: string }[] = [];
  groupOptions: { label: string; value: string }[] = [];

  // HR Data
  employees: EmployeeListItem[] = [];
  employeeOptions: { label: string; value: number }[] = [];
  branches: BranchListItem[] = [];
  branchOptions: { label: string; value: number }[] = [];
  insuranceCompanies: InsuranceCompanyListItem[] = [];
  insuranceCompanyOptions: { label: string; value: number }[] = [];
  portalEntities: ExternalEntityListItem[] = [];
  portalEntityOptions: { label: string; value: number }[] = [];
  systemApps: SystemApp[] = [];
  systemOptions: { label: string; value: string }[] = [];

  // User Details Drawer
  detailsVisible = signal(false);
  selectedUser = signal<UserDetails | null>(null);
  detailsLoading = signal(false);
  detailsTabIndex = 0;

  // Details tabs data
  auditLogs = signal<AuditLog[]>([]);
  auditTotal = signal(0);
  auditLoading = signal(false);
  userSessions = signal<UserSession[]>([]);
  sessionsLoading = signal(false);

  // Add/Edit Form
  formVisible = false;
  formMode: 'create' | 'edit' = 'create';
  form: CreateUserRequest = {
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isActive: true,
    password: '',
  };
  formSelectedRoles: string[] = [];
  formSelectedGroups: string[] = [];
  formSelectedEmployeeId: number | null = null;
  formSelectedBranchIds: number[] = [];
  /** شركة التأمين في CuraLab — شاشة مستخدمي التأمين */
  formSelectedInsuranceCompanyId: number | null = null;
  formSelectedPortalEntityId: number | null = null;
  formSelectedSystemKeys: string[] = [];
  formLoading = signal(false);
  formSubmitted = false;

  // Bulk selection
  selectedUsers = signal<UserListItem[]>([]);

  // Password reset dialog
  passwordResetVisible = false;
  passwordResetUserId = signal('');
  passwordResetPassword = '';
  passwordResetLoading = signal(false);

  ngOnInit() {
    const d = this.route.snapshot.data;
    const pk = d['portalSystemKey'];
    if (typeof pk === 'string' && pk.trim()) {
      this.portalSystemKey.set(pk.trim());
    }
    const entityType = d['portalEntityType'];
    if (entityType === 'patient' || entityType === 'doctor' || entityType === 'referrallab') {
      this.portalEntityType.set(entityType);
    }
    this.relaxedEmployeeBranch.set(!!d['relaxedEmployeeBranch']);
    const t = d['pageTitleKey'];
    if (typeof t === 'string' && t) this.pageTitleKey.set(t);
    const st = d['pageSubtitleKey'];
    if (typeof st === 'string' && st) this.pageSubtitleKey.set(st);

    this.loadRolesAndGroups();
    this.loadEmployeesAndBranches();
    if (this.portalSystemKey() === 'insurance') {
      this.loadInsuranceCompanies();
    }
    if (this.portalEntityType()) {
      this.loadPortalEntities();
    }
    this.loadSystemApps();
    this.loadUsers();
  }

  private isInsuranceUsersPortal(): boolean {
    return this.portalSystemKey() === 'insurance';
  }

  isPortalEntityUsersPortal(): boolean {
    return this.portalEntityType() !== null;
  }

  get portalEntityLabelKey(): string {
    switch (this.portalEntityType()) {
      case 'patient': return 'PATIENT_USERS.ENTITY_LABEL';
      case 'doctor': return 'DOCTOR_USERS.ENTITY_LABEL';
      case 'referrallab': return 'REFERRAL_LAB_USERS.ENTITY_LABEL';
      default: return '';
    }
  }

  get portalEntityPlaceholderKey(): string {
    switch (this.portalEntityType()) {
      case 'patient': return 'PATIENT_USERS.ENTITY_PLACEHOLDER';
      case 'doctor': return 'DOCTOR_USERS.ENTITY_PLACEHOLDER';
      case 'referrallab': return 'REFERRAL_LAB_USERS.ENTITY_PLACEHOLDER';
      default: return '';
    }
  }

  get portalEntityHintKey(): string {
    switch (this.portalEntityType()) {
      case 'patient': return 'PATIENT_USERS.ENTITY_HINT';
      case 'doctor': return 'DOCTOR_USERS.ENTITY_HINT';
      case 'referrallab': return 'REFERRAL_LAB_USERS.ENTITY_HINT';
      default: return '';
    }
  }

  get portalEntityRequiredKey(): string {
    switch (this.portalEntityType()) {
      case 'patient': return 'PATIENT_USERS.ENTITY_REQUIRED';
      case 'doctor': return 'DOCTOR_USERS.ENTITY_REQUIRED';
      case 'referrallab': return 'REFERRAL_LAB_USERS.ENTITY_REQUIRED';
      default: return '';
    }
  }

  get portalEntitySectionKey(): string {
    switch (this.portalEntityType()) {
      case 'patient': return 'PATIENT_USERS.ENTITY_SECTION';
      case 'doctor': return 'DOCTOR_USERS.ENTITY_SECTION';
      case 'referrallab': return 'REFERRAL_LAB_USERS.ENTITY_SECTION';
      default: return '';
    }
  }

  getPortalEntityId(user: UserListItem): number | null {
    switch (this.portalEntityType()) {
      case 'patient': return user.labPatientId ?? null;
      case 'doctor': return user.labDoctorId ?? null;
      case 'referrallab': return user.labReferralLabId ?? null;
      default: return null;
    }
  }

  getPortalEntityDisplayName(user: UserListItem): string | null {
    switch (this.portalEntityType()) {
      case 'patient': return user.labPatientDisplayName ?? null;
      case 'doctor': return user.labDoctorDisplayName ?? null;
      case 'referrallab': return user.labReferralLabDisplayName ?? null;
      default: return null;
    }
  }

  getSelectedPortalEntityId(): number | null {
    const user = this.selectedUser();
    if (!user) return null;
    switch (this.portalEntityType()) {
      case 'patient': return user.labPatientId ?? null;
      case 'doctor': return user.labDoctorId ?? null;
      case 'referrallab': return user.labReferralLabId ?? null;
      default: return null;
    }
  }

  getSelectedPortalEntityName(): string | null {
    const user = this.selectedUser();
    if (!user) return null;
    switch (this.portalEntityType()) {
      case 'patient': return user.labPatientNameAr || user.labPatientNameEn || null;
      case 'doctor': return user.labDoctorNameAr || user.labDoctorNameEn || null;
      case 'referrallab': return user.labReferralLabNameAr || user.labReferralLabNameEn || null;
      default: return null;
    }
  }

  loadSystemApps() {
    this.api.listSystems().subscribe({
      next: (systems) => {
        this.systemApps = systems.filter((s) => s.isActive !== false);
        this.systemOptions = this.systemApps.map((s) => ({
          label: `${s.displayName} (${s.key})`,
          value: s.key,
        }));
      },
      error: (err) => console.error('Error loading systems:', err),
    });
  }

  loadRolesAndGroups() {
    this.api.listRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        this.roleOptions = roles.map((r) => ({
          label: r.name + (r.systemKey ? ` (${r.systemKey})` : ''),
          value: r.id!,
        }));
        this.applyDefaultPortalRoleForCreate();
      },
      error: (err) => {
        console.error('Error loading roles:', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحميل الأدوار',
        });
      },
    });

    this.api.listGroups().subscribe({
      next: (groups) => {
        this.groups = groups;
        this.groupOptions = groups.map((g) => ({
          label: g.name + (g.systemKey ? ` (${g.systemKey})` : ''),
          value: g.id!,
        }));
      },
      error: (err) => {
        console.error('Error loading groups:', err);
      },
    });
  }

  loadEmployeesAndBranches() {
    this.api.listEmployees().subscribe({
      next: (employees) => {
        this.employees = employees;
        this.employeeOptions = employees.map((e) => ({
          label: `${e.fullNameAr}${e.employeeJobNumber ? ' (' + e.employeeJobNumber + ')' : ''}`,
          value: e.id,
        }));
      },
      error: (err) => {
        console.error('Error loading employees:', err);
      },
    });

    this.api.listBranches().subscribe({
      next: (branches) => {
        this.branches = branches;
        this.branchOptions = branches.map((b) => ({
          label: `${b.nameAr}${b.code ? ' (' + b.code + ')' : ''}`,
          value: b.id,
        }));
      },
      error: (err) => {
        console.error('Error loading branches:', err);
      },
    });
  }

  /**
   * عند شاشة التأمين: يسحب الشركات من CuraLab (عبر Auth) ثم يحدّث القائمة من المرآة.
   * @param silentToasts عند true (مثل فتح نموذج الإضافة) لا نعرض رسالة النجاح لتقليل الإزعاج.
   */
  loadInsuranceCompanies(silentToasts = false) {
    this.api
      .importInsuranceCompaniesFromLab()
      .pipe(
        tap((r) => {
          if (r?.warning) {
            this.ms.add({ severity: 'warn', summary: 'تنبيه', detail: r.warning });
          } else if (!silentToasts && r && r.upserted > 0) {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: `تم مزامنة ${r.upserted} شركة تأمين من المختبر`,
            });
          }
        }),
        catchError((err) => {
          console.error('Insurance import from Lab:', err);
          this.ms.add({
            severity: 'warn',
            summary: 'تنبيه',
            detail: 'تعذر سحب القائمة من المختبر — سيتم عرض ما هو محفوظ في Auth إن وُجد.',
          });
          return of(null);
        }),
        switchMap(() => this.api.listInsuranceCompanies())
      )
      .subscribe({
        next: (list) => {
          this.insuranceCompanies = list;
          this.insuranceCompanyOptions = list.map((c) => ({
            label: `${c.nameAr || c.nameEn}${c.nameEn && c.nameAr !== c.nameEn ? ' / ' + c.nameEn : ''}`,
            value: c.id,
          }));
        },
        error: (err) => {
          console.error('Error loading insurance companies:', err);
          this.ms.add({
            severity: 'error',
            summary: 'خطأ',
            detail: 'فشل تحميل قائمة شركات التأمين',
          });
        },
      });
  }

  loadPortalEntities(silentToasts = false) {
    const entityType = this.portalEntityType();
    if (!entityType) return;

    const import$ =
      entityType === 'patient'
        ? this.api.importPatientsFromLab()
        : entityType === 'doctor'
          ? this.api.importDoctorsFromLab()
          : this.api.importReferralLabsFromLab();

    const list$ =
      entityType === 'patient'
        ? this.api.listPatients()
        : entityType === 'doctor'
          ? this.api.listDoctors()
          : this.api.listReferralLabs();

    import$
      .pipe(
        tap((r) => {
          if (r?.warning) {
            this.ms.add({ severity: 'warn', summary: 'تنبيه', detail: r.warning });
          } else if (!silentToasts && r && r.upserted > 0) {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: `تمت مزامنة ${r.upserted} سجل من المختبر`,
            });
          }
        }),
        catchError((err) => {
          console.error('Portal entity import from Lab:', err);
          this.ms.add({
            severity: 'warn',
            summary: 'تنبيه',
            detail: 'تعذر سحب القائمة من المختبر - سيتم عرض ما هو محفوظ في Auth إن وجد.',
          });
          return of(null);
        }),
        switchMap(() => list$)
      )
      .subscribe({
        next: (list) => {
          this.portalEntities = list;
          this.portalEntityOptions = list.map((item) => ({
            label: `${item.nameAr || item.nameEn}${item.nameEn && item.nameAr !== item.nameEn ? ' / ' + item.nameEn : ''}`,
            value: item.id,
          }));
        },
        error: (err) => {
          console.error('Error loading portal entities:', err);
          this.ms.add({
            severity: 'error',
            summary: 'خطأ',
            detail: 'فشل تحميل قائمة الربط',
          });
        },
      });
  }

  loadUsers() {
    this.loading.set(true);

    const params: any = {
      page: this.pagination.page,
      pageSize: this.pagination.rows,
      sortField: this.pagination.sortField,
      sortOrder: this.pagination.sortOrder,
    };

    if (this.filters.search) params.search = this.filters.search;
    if (this.filters.status) params.status = this.filters.status;
    if (this.filters.roleIds.length) params.roleIds = this.filters.roleIds.join(',');
    if (this.filters.groupIds.length) params.groupIds = this.filters.groupIds.join(',');
    if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
    if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
    if (this.filters.lastLoginFrom) params.lastLoginFrom = this.filters.lastLoginFrom;
    if (this.filters.lastLoginTo) params.lastLoginTo = this.filters.lastLoginTo;
    if (this.filters.includeDeleted || this.filters.status === 'deleted') params.includeDeleted = true;
    const portalKey = this.portalSystemKey();
    if (portalKey) {
      params.portalSystemKey = portalKey;
    }
    if (portalKey === 'insurance') {
      params.insuranceUsersOnly = true;
    } else if (!portalKey) {
      params.excludePortalUsers = true;
    }

    this.api.listUsers(params).subscribe({
      next: (result: PagedResult<UserListItem>) => {
        this.users.set(result.items || []);
        this.totalRecords.set(result.total || 0);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading users:', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحميل المستخدمين',
        });
        this.loading.set(false);
      },
    });
  }

  onPageChange(event: any) {
    this.pagination.first = event.first;
    this.pagination.rows = event.rows;
    this.pagination.page = Math.floor(event.first / event.rows) + 1;
    this.loadUsers();
  }

  onSort(event: any) {
    this.pagination.sortField = event.field;
    this.pagination.sortOrder = event.order === 1 ? 'asc' : 'desc';
    this.pagination.first = 0;
    this.pagination.page = 1;
    this.loadUsers();
  }

  applyFilters() {
    this.pagination.first = 0;
    this.pagination.page = 1;
    this.loadUsers();
  }

  clearFilters() {
    this.filters = {
      search: '',
      status: '',
      roleIds: [],
      groupIds: [],
      dateFrom: '',
      dateTo: '',
      lastLoginFrom: '',
      lastLoginTo: '',
      includeDeleted: false,
    };
    this.applyFilters();
  }

  // User Details
  openDetails(userId: string) {
    this.detailsVisible.set(true);
    this.detailsLoading.set(true);
    this.detailsTabIndex = 0;

    this.api.getUser(userId).subscribe({
      next: (user) => {
        this.selectedUser.set(user);
        this.detailsLoading.set(false);
        this.loadUserDetails();
      },
      error: (err) => {
        console.error('Error loading user details:', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحميل بيانات المستخدم',
        });
        this.detailsLoading.set(false);
      },
    });
  }

  loadUserDetails() {
    const userId = this.selectedUser()?.id;
    if (!userId) return;

    // Load audit logs
    this.auditLoading.set(true);
    this.api.getUserAuditLogs(userId, { page: 1, pageSize: 50 }).subscribe({
      next: (result) => {
        this.auditLogs.set(result.items || []);
        this.auditTotal.set(result.total || 0);
        this.auditLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading audit logs:', err);
        this.auditLoading.set(false);
      },
    });

    // Load sessions
    this.sessionsLoading.set(true);
    this.api.getUserSessions(userId).subscribe({
      next: (sessions) => {
        this.userSessions.set(sessions || []);
        this.sessionsLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading sessions:', err);
        this.sessionsLoading.set(false);
      },
    });
  }

  // Add/Edit User
  openAdd() {
    if (this.isInsuranceUsersPortal()) {
      this.loadInsuranceCompanies(true);
    }
    if (this.isPortalEntityUsersPortal()) {
      this.loadPortalEntities(true);
    }
    this.formMode = 'create';
    this.form = {
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isActive: true,
      password: '',
      employeeId: undefined,
    };
    this.formSelectedRoles = [];
    this.formSelectedGroups = [];
    this.formSelectedEmployeeId = null;
    this.formSelectedBranchIds = [];
    this.formSelectedInsuranceCompanyId = null;
    this.formSelectedPortalEntityId = null;
    const pk = this.portalSystemKey();
    this.formSelectedSystemKeys = pk ? [pk] : [];
    this.applyDefaultPortalRoleForCreate();
    this.formSubmitted = false;
    this.formVisible = true;
  }

  private defaultRoleNameForPortal(systemKey: string): string | null {
    switch (systemKey.toLowerCase()) {
      case 'doctor':
        return 'DoctorPortalFullAccess';
      case 'patient':
        return 'PatientPortalFullAccess';
      case 'referrallab':
        return 'ReferralLabPortalFullAccess';
      case 'insurance':
        return 'InsurancePortalFullAccess';
      default:
        return null;
    }
  }

  private getDefaultPortalRoleId(): string | null {
    const portalKey = this.portalSystemKey()?.trim().toLowerCase();
    if (!portalKey) return null;

    const candidates = this.roles.filter(
      (role) => !!role.id && role.systemKey?.trim().toLowerCase() === portalKey
    );
    if (!candidates.length) return null;

    const defaultName = this.defaultRoleNameForPortal(portalKey);
    const exactRole = defaultName ? candidates.find((role) => role.name === defaultName) : undefined;
    const fullAccessRole = candidates.find((role) => role.name.toLowerCase().includes('fullaccess'));

    return (exactRole ?? fullAccessRole ?? candidates[0]).id ?? null;
  }

  private applyDefaultPortalRoleForCreate(): void {
    if (this.formMode !== 'create') return;

    const roleId = this.getDefaultPortalRoleId();
    if (!roleId || this.formSelectedRoles.includes(roleId)) return;

    this.formSelectedRoles = [...this.formSelectedRoles, roleId];
  }

  openEdit(user: UserListItem) {
    if (this.isInsuranceUsersPortal()) {
      this.loadInsuranceCompanies(true);
    }
    if (this.isPortalEntityUsersPortal()) {
      this.loadPortalEntities(true);
    }
    this.formMode = 'edit';
    this.detailsLoading.set(true);

    this.api.getUser(user.id).subscribe({
      next: (userDetails) => {
        this.form = {
          username: userDetails.username,
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          email: userDetails.email || '',
          phone: userDetails.phone || '',
          isActive: userDetails.isActive,
        };
        this.formSelectedRoles = this.roles
          .filter((r) => userDetails.roles?.includes(r.name))
          .map((r) => r.id!);
        this.formSelectedGroups = this.groups
          .filter((g) => userDetails.groups?.includes(g.name))
          .map((g) => g.id!);
        this.formSelectedEmployeeId = userDetails.employeeId || null;
        // Ensure branchIds is an array, not undefined
        this.formSelectedBranchIds = userDetails.branchIds ? [...userDetails.branchIds] : [];
        this.formSelectedInsuranceCompanyId = userDetails.labInsuranceCompanyId ?? null;
        this.formSelectedPortalEntityId =
          this.portalEntityType() === 'patient'
            ? userDetails.labPatientId ?? null
            : this.portalEntityType() === 'doctor'
              ? userDetails.labDoctorId ?? null
              : this.portalEntityType() === 'referrallab'
                ? userDetails.labReferralLabId ?? null
                : null;
        this.formSelectedSystemKeys = userDetails.allowedSystemKeys
          ? [...userDetails.allowedSystemKeys]
          : [];
        console.log('Loaded branchIds for edit:', this.formSelectedBranchIds);
        this.formSubmitted = false;
        this.formVisible = true;
        this.detailsLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading user for edit:', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحميل بيانات المستخدم',
        });
        this.detailsLoading.set(false);
      },
    });
  }

  saveUser() {
    this.formSubmitted = true;

    // Validate username
    if (!this.form.username?.trim()) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: 'اسم المستخدم مطلوب',
      });
      this.formLoading.set(false);
      return;
    }

    // Validate password for new users
    if (this.formMode === 'create' && !this.form.password?.trim()) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: 'كلمة المرور مطلوبة للمستخدم الجديد',
      });
      this.formLoading.set(false);
      return;
    }

    const relaxed = this.relaxedEmployeeBranch();

    // Validate employee (required unless insurance portal mode)
    if (!relaxed && !this.formSelectedEmployeeId) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: 'يجب اختيار موظف للمستخدم',
      });
      this.formLoading.set(false);
      return;
    }

    // Validate branches (required unless insurance portal mode)
    if (!relaxed && (!this.formSelectedBranchIds || this.formSelectedBranchIds.length === 0)) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: 'يجب اختيار فرع واحد على الأقل للمستخدم',
      });
      this.formLoading.set(false);
      return;
    }

    if (this.isInsuranceUsersPortal() && !this.formSelectedInsuranceCompanyId) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: 'يجب اختيار شركة التأمين المرتبطة في المختبر',
      });
      this.formLoading.set(false);
      return;
    }

    const portalKey = this.portalSystemKey();
    if (this.isPortalEntityUsersPortal() && !this.formSelectedPortalEntityId) {
      this.ms.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Portal entity is required',
      });
      this.formLoading.set(false);
      return;
    }
    const mergedSystemKeys = [...(this.formSelectedSystemKeys ?? [])];
    if (portalKey && !mergedSystemKeys.includes(portalKey)) {
      mergedSystemKeys.push(portalKey);
    }

    this.formLoading.set(true);

    if (this.formMode === 'create') {
      const createRequest: CreateUserRequest = {
        username: this.form.username.trim(),
        firstName: this.form.firstName?.trim() || undefined,
        lastName: this.form.lastName?.trim() || undefined,
        email: this.form.email?.trim() || undefined,
        phone: this.form.phone?.trim() || undefined,
        isActive: this.form.isActive,
        password: this.form.password?.trim() || undefined,
        employeeId: this.formSelectedEmployeeId || undefined,
        allowedSystemKeys: mergedSystemKeys.length > 0 ? mergedSystemKeys : undefined,
      };
      if (this.isInsuranceUsersPortal() && this.formSelectedInsuranceCompanyId) {
        createRequest.labInsuranceCompanyId = this.formSelectedInsuranceCompanyId;
      }
      this.applyPortalEntityToRequest(createRequest);

      this.api.createUser(createRequest).subscribe({
        next: (user) => {
          // Link employee if selected
          const employeeLink$ = this.formSelectedEmployeeId
            ? this.api.linkUserEmployee(user.id, this.formSelectedEmployeeId).toPromise()
            : Promise.resolve(null);

          // Assign branches - always call this to ensure branches are updated even if empty array
          const branches$ = this.api.setUserBranches(user.id, this.formSelectedBranchIds || []).toPromise();

          // Assign roles
          const roles$ = this.formSelectedRoles.length
            ? this.api.setUserRoles(user.id, this.formSelectedRoles).toPromise()
            : Promise.resolve(null);

          // Assign groups
          const groups$ = this.formSelectedGroups.length
            ? this.api.setUserGroups(user.id, this.formSelectedGroups).toPromise()
            : Promise.resolve(null);

          Promise.all([employeeLink$, branches$, roles$, groups$])
            .then(() => this.finishSave())
            .catch((err) => this.handleSaveError(err, true));
        },
        error: (err) => this.handleSaveError(err),
      });
    } else {
      const userId = this.selectedUser()?.id || this.users().find((u) => u.username === this.form.username)?.id;
      if (!userId) {
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'المستخدم غير موجود' });
        this.formLoading.set(false);
        return;
      }

      const updateRequest: UpdateUserRequest = {
        username: this.form.username.trim(),
        firstName: this.form.firstName?.trim() || undefined,
        lastName: this.form.lastName?.trim() || undefined,
        email: this.form.email?.trim() || undefined,
        phone: this.form.phone?.trim() || undefined,
        isActive: this.form.isActive,
        employeeId: this.formSelectedEmployeeId || undefined,
        allowedSystemKeys: mergedSystemKeys,
      };
      if (this.isInsuranceUsersPortal()) {
        updateRequest.labInsuranceCompanyId = this.formSelectedInsuranceCompanyId;
      }
      this.applyPortalEntityToRequest(updateRequest);

      this.api.updateUser(userId, updateRequest).subscribe({
        next: () => {
          console.log('Updating user with branchIds:', this.formSelectedBranchIds);
          // Link/unlink employee
          const employeeLink$ = this.api.linkUserEmployee(userId, this.formSelectedEmployeeId).toPromise();

          // Update branches - always call this to ensure branches are updated even if empty array
          const branches$ = this.api.setUserBranches(userId, this.formSelectedBranchIds || []).toPromise();

          // Update roles
          const roles$ = this.api.setUserRoles(userId, this.formSelectedRoles).toPromise();

          // Update groups
          const groups$ = this.api.setUserGroups(userId, this.formSelectedGroups).toPromise();

          Promise.all([employeeLink$, branches$, roles$, groups$])
            .then(() => {
              console.log('All updates completed successfully');
              this.finishSave();
            })
            .catch((err) => {
              console.error('Error in parallel updates:', err);
              this.handleSaveError(err, true);
            });
        },
        error: (err) => this.handleSaveError(err),
      });
    }
  }

  private applyPortalEntityToRequest(request: CreateUserRequest | UpdateUserRequest): void {
    const id = this.formSelectedPortalEntityId;
    if (id === null) return;
    switch (this.portalEntityType()) {
      case 'patient':
        request.labPatientId = id;
        break;
      case 'doctor':
        request.labDoctorId = id;
        break;
      case 'referrallab':
        request.labReferralLabId = id;
        break;
    }
  }

  finishSave() {
    this.ms.add({
      severity: 'success',
      summary: 'تم',
      detail: `تم ${this.formMode === 'create' ? 'إنشاء' : 'تحديث'} المستخدم بنجاح`,
    });
    this.formVisible = false;
    this.formLoading.set(false);
    this.formSubmitted = false;
    // Reload users to get updated data including branches
    this.loadUsers();
    // If we have a selected user in details view, reload it too
    const selectedUserId = this.selectedUser()?.id;
    if (selectedUserId) {
      this.api.getUser(selectedUserId).subscribe({
        next: (user) => {
          this.selectedUser.set(user);
        },
        error: (err) => console.error('Error reloading user details:', err),
      });
    }
  }

  handleSaveError(err: any, partial = false) {
    console.error('Error saving user:', err);
    const errorMsg = err?.error?.message || err?.error || 'فشل الحفظ';
    this.ms.add({
      severity: partial ? 'warn' : 'error',
      summary: partial ? 'تحذير' : 'خطأ',
      detail: partial ? 'تم الحفظ جزئياً: ' + errorMsg : errorMsg,
    });
    this.formLoading.set(false);
    if (!partial) {
      this.formVisible = false;
      this.formSubmitted = false;
    }
    this.loadUsers();
  }

  // User Actions
  toggleStatus(user: UserListItem) {
    this.confirmService.confirm({
      message: `هل تريد ${user.isActive ? 'تعطيل' : 'تفعيل'} هذا المستخدم؟`,
      header: 'تأكيد',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.api.setUserStatus(user.id, !user.isActive).subscribe({
          next: () => {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: `تم ${user.isActive ? 'تعطيل' : 'تفعيل'} المستخدم بنجاح`,
            });
            this.loadUsers();
          },
          error: (err) => {
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: 'فشل تحديث حالة المستخدم',
            });
          },
        });
      },
    });
  }

  toggleLock(user: UserListItem) {
    this.confirmService.confirm({
      message: `هل تريد ${user.isLocked ? 'فتح' : 'قفل'} هذا المستخدم؟`,
      header: 'تأكيد',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.api.setUserLock(user.id, !user.isLocked).subscribe({
          next: () => {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: `تم ${user.isLocked ? 'فتح' : 'قفل'} المستخدم بنجاح`,
            });
            this.loadUsers();
          },
          error: (err) => {
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: 'فشل تحديث حالة القفل',
            });
          },
        });
      },
    });
  }

  openPasswordReset(user: UserListItem) {
    this.passwordResetUserId.set(user.id);
    this.passwordResetPassword = '';
    this.passwordResetVisible = true;
  }

  generateRandomPassword() {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%^&*?-_=+';
    const all = upper + lower + digits + symbols;

    const pick = (pool: string) => pool[Math.floor(Math.random() * pool.length)];
    const chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];
    for (let i = chars.length; i < 14; i++) chars.push(pick(all));

    // Fisher–Yates shuffle so guaranteed chars aren't always at the start.
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }

    this.passwordResetPassword = chars.join('');
  }

  copyPasswordResetValue() {
    if (!this.passwordResetPassword) return;
    navigator.clipboard?.writeText(this.passwordResetPassword).then(
      () => this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم نسخ كلمة المرور' }),
      () => this.ms.add({ severity: 'warn', summary: 'تنبيه', detail: 'تعذّر النسخ' }),
    );
  }

  resetPassword() {
    if (!this.passwordResetPassword || !this.passwordResetPassword.trim()) {
      this.ms.add({
        severity: 'warn',
        summary: 'تحذير',
        detail: 'كلمة المرور مطلوبة',
      });
      return;
    }

    this.passwordResetLoading.set(true);
    this.api.adminResetPassword(this.passwordResetUserId(), this.passwordResetPassword).subscribe({
      next: () => {
        this.ms.add({
          severity: 'success',
          summary: 'تم',
          detail: 'تم تعيين كلمة المرور بنجاح',
        });
        this.passwordResetVisible = false;
        this.passwordResetLoading.set(false);
      },
      error: (err) => {
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: this.extractErrorMessage(err, 'فشل تعيين كلمة المرور'),
        });
        this.passwordResetLoading.set(false);
      },
    });
  }

  /**
   * Pull the most useful message out of an HttpErrorResponse:
   * - Plain { message } from BadRequest
   * - RFC 7807 ProblemDetails { errors: { field: string[] } } from ValidationProblem
   * - Bare string body
   */
  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (!body) return fallback;
    if (typeof body === 'string') return body;
    if (body.message) return body.message;
    if (body.errors && typeof body.errors === 'object') {
      const messages: string[] = [];
      for (const value of Object.values(body.errors)) {
        if (Array.isArray(value)) {
          for (const m of value) {
            if (typeof m === 'string') messages.push(m);
          }
        } else if (typeof value === 'string') {
          messages.push(value);
        }
      }
      if (messages.length > 0) return messages.join(' • ');
    }
    if (body.title) return body.title;
    return fallback;
  }

  deleteUser(user: UserListItem) {
    this.confirmService.confirm({
      message: 'هل أنت متأكد من حذف هذا المستخدم؟ سيتم الحذف بشكل منطقي (soft delete).',
      header: 'تأكيد الحذف',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.api.deleteUser(user.id).subscribe({
          next: () => {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: 'تم حذف المستخدم بنجاح',
            });
            this.loadUsers();
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'فشل حذف المستخدم';
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: errorMsg,
            });
          },
        });
      },
    });
  }

  restoreUser(user: UserListItem) {
    this.confirmService.confirm({
      message: 'هل أنت متأكد من استرجاع هذا المستخدم؟',
      header: 'تأكيد الاسترجاع',
      icon: 'pi pi-undo',
      accept: () => {
        this.api.restoreUser(user.id).subscribe({
          next: () => {
            this.ms.add({
              severity: 'success',
              summary: 'تم',
              detail: 'تم استرجاع المستخدم بنجاح',
            });
            this.loadUsers();
          },
          error: (err) => {
            const errorMsg = err?.error?.message || 'فشل استرجاع المستخدم';
            this.ms.add({
              severity: 'error',
              summary: 'خطأ',
              detail: errorMsg,
            });
          },
        });
      },
    });
  }

  // Bulk Actions
  bulkActivate() {
    if (this.selectedUsers().length === 0) return;
    // Implementation would batch API calls
    this.ms.add({ severity: 'info', summary: 'معلومات', detail: 'ميزة التفعيل الجماعي قيد التطوير' });
  }

  bulkDeactivate() {
    if (this.selectedUsers().length === 0) return;
    this.ms.add({ severity: 'info', summary: 'معلومات', detail: 'ميزة التعطيل الجماعي قيد التطوير' });
  }

  // Utility functions
  getStatusSeverity(user: UserListItem): 'success' | 'warning' | 'danger' | 'info' {
    if (user.isDeleted) return 'danger';
    if (!user.isActive) return 'warning';
    if (user.isLocked) return 'danger';
    return 'success';
  }

  getStatusLabel(user: UserListItem): string {
    if (user.isDeleted) return 'محذوف';
    if (!user.isActive) return 'معطل';
    if (user.isLocked) return 'مقفل';
    return 'نشط';
  }

  formatDate(date?: string): string {
    if (!date) return '—';
    return new Date(date).toLocaleString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getEmployeeName(employeeId: number): string | null {
    const employee = this.employees.find(e => e.id === employeeId);
    return employee ? employee.fullNameAr : null;
  }

  getBranchName(branchId: number): string {
    const branch = this.branches.find(b => b.id === branchId);
    return branch ? branch.nameAr : `#${branchId}`;
  }

  branchNamesTooltip(branchIds: number[]): string {
    return branchIds.map(id => this.getBranchName(id)).join('، ');
  }
}
